import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import '@shared/i18n';
import './styles/index.css';
import App from './app/App';

/**
 * iter-31.3 / iter-49: stale-chunk auto-recovery.
 *
 * When a new deployment ships, Vite emits new content-hashed chunk
 * filenames. A user who has the previous `index.html` cached (via
 * browser cache or the PWA service worker) will try to dynamic-import
 * the old chunk name on the next route change. Two failure modes:
 *
 *   - Browser sees 404 from origin → "Failed to fetch dynamically
 *     imported module" (network reject).
 *   - SW/Vercel serves the SPA HTML fallback for the missing chunk → the
 *     browser tries to execute HTML as an ES module and refuses with
 *     "Failed to load module script: Expected a JavaScript-or-Wasm
 *     module script but the server responded with a MIME type of
 *     'text/html'". This pattern wasn't recognised by iter-31.3 alone.
 *
 * Recovery: when we detect either, we unregister the active service
 * worker and clear its caches so the next navigation goes straight to
 * the origin with the fresh `index.html`. A plain reload() still hits
 * the same stale SW and loops, which is why iter-31.3 wasn't enough on
 * its own. SessionStorage gates the attempt so a non-stale-chunk
 * failure can't infinite-loop reloads.
 */
const RELOAD_KEY = 'yusr.stale-chunk-reloaded';
async function reloadOnce(): Promise<void> {
  try {
    if (sessionStorage.getItem(RELOAD_KEY) === '1') {
      // Already tried recovery once this session — don't spam the user.
      return;
    }
    sessionStorage.setItem(RELOAD_KEY, '1');
  } catch {
    // SessionStorage might be unavailable (private mode); just reload.
  }
  // iter-49: drop the stale SW + caches so the post-reload fetch goes
  // to the origin with the up-to-date HTML, not whatever the previous
  // SW had pinned. Without this, the reload re-installs the same stale
  // HTML and the MIME-mismatch error returns immediately.
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch {
    // Whatever — proceed to reload anyway, even a partial recovery is
    // better than getting stuck.
  }
  window.location.reload();
}
function isChunkError(message: string | null | undefined): boolean {
  if (!message) return false;
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    /Loading (chunk|CSS chunk) [^\s]+ failed/.test(message) ||
    message.includes('error loading dynamically imported module') ||
    // iter-49: SPA-fallback path. SW served index.html for a chunk URL,
    // browser refused to execute HTML as a module.
    message.includes('Failed to load module script') ||
    message.includes('MIME type of "text/html"') ||
    message.includes("MIME type of 'text/html'")
  );
}
/**
 * iter-62: detect the React-vs-Google-Maps teardown crash that bit
 * /volunteer/places/submit live. The user-facing symptom is a generic
 * "Unexpected Application Error! The object can not be found here."
 * page that doesn't go away on reload because the buggy chunk is
 * cached by the PWA's service worker. iter-49's recovery only fires
 * for chunk-load failures; this is a runtime DOM error inside an
 * already-loaded chunk, so it slips past.
 *
 * Pattern: any unhandled error whose message mentions removeChild and
 * the "object can not be found" / "not a child" wording from
 * `Node.removeChild()` failing across browsers (WebKit's
 * "NotFoundError: The object can not be found here.", Blink's
 * "Failed to execute 'removeChild' on 'Node': The node to be removed
 * is not a child of this node.").
 *
 * Treat it the same as a stale-chunk error: unregister the SW + clear
 * caches + reload. Anyone running a buggy build gets ejected and
 * re-served the latest one without having to clear cache by hand.
 */
function isStaleTeardownError(message: string | null | undefined): boolean {
  if (!message) return false;
  return (
    (message.includes('removeChild') &&
      (message.includes('not a child') || message.includes('object can not be found') || message.includes('NotFoundError'))) ||
    // Standalone WebKit phrasing seen on iOS Safari during the
    // /volunteer/places/submit crash — no "removeChild" in the message
    // string itself, only in the stack trace.
    message.includes('The object can not be found here')
  );
}
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  void reloadOnce();
});
window.addEventListener('error', (event) => {
  const errMessage = event.message ?? (event.error as Error | null)?.message;
  if (isChunkError(event.message) || isChunkError((event.error as Error | null)?.message)) {
    void reloadOnce();
    return;
  }
  if (isStaleTeardownError(errMessage)) {
    void reloadOnce();
  }
});
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason as { message?: string } | string | null;
  const message = typeof reason === 'string' ? reason : reason?.message;
  if (isChunkError(message)) {
    void reloadOnce();
    return;
  }
  if (isStaleTeardownError(message)) {
    void reloadOnce();
  }
});

const container = document.getElementById('root');
if (!container) throw new Error('Root container #root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
